<template>
  <v-list dense style="padding: 30px">
    <v-list-item-content
      class="pa-0"
      v-for="item in filters"
      :key="item.title"
      link
    >
      <v-list-item-content class="pa-0">
        <v-list-item-title class="d-inline-flex align-center">
          <v-list-item-icon class="d-flex justify-center align-center">
            <v-icon class="pr-4">{{ item.icon }}</v-icon>
            {{ $t(item.translationPath) }}
          </v-list-item-icon>
        </v-list-item-title>
        <v-row>
          <v-col cols="12">
            <!-- Report Type Filter -->
            <v-autocomplete
              v-if="item.type === 'reportTypes'"
              :no-data-text="$t('common.fields.noDataAvailable')"
              small
              flat
              solo
              background-color="#EAEAEA80"
              color="primary"
              clearable
              item-color="primary"
              :items="item.options"
              item-text="title"
              item-value="type"
              v-model="customReportFilters.reportTypes"
              small-chips
              :label="$t(item.placeholder)"
              multiple
            >
              <template v-slot:selection="{ item:option }">
                <v-chip>
                  <span>{{ option.title }}</span>
                </v-chip>
              </template>
            </v-autocomplete>

            <!-- Unique ID Field -->
            <v-text-field
              v-if="item.type === 'uniqueId'"
              small
              flat
              solo
              background-color="#EAEAEA80"
              color="primary"
              :label="$t('records.fields.code')"
              clearable
              v-mask="item.mask"
              v-model="customReportFilters.uniqueId"
            ></v-text-field>

            <!-- Latest Reports Filter -->
            <v-autocomplete
              v-if="item.type === 'latest'"
              :no-data-text="$t('common.fields.noDataAvailable')"
              small
              flat
              solo
              background-color="#EAEAEA80"
              color="primary"
              clearable
              item-color="primary"
              :items="item.options"
              item-text="name"
              item-value="value"
              v-model="customReportFilters.latest"
              small-chips
              :label="$t(item.placeholder)"
            >
              <template v-slot:selection="{ item:option }">
                <v-chip>
                  <span>{{ option.name }}</span>
                </v-chip>
              </template>
            </v-autocomplete>

            <!-- Lot Filter -->
            <v-list v-if="item.type === 'lots'" class="mb-6">
                <AdvancedFilterComponent
                  v-model="selectedLots"
                  :items="items" 
                  :loading="loading"
                  :multiple="true"
                  item-text="name"
                  item-value="name"
                  :return-object="false"
                  :search-label="$t('home.fields.search')"
                  chip-group-id="lots"
                  :show-selected-chips="false"
                  :total-selected-text="$t('inventory.titles.total')"
                  @change="onLotDataChange"
                  @search="handleSearch"
                  @page-change="handlePageChange"
                  @clear="clearSelectedLots"
                  style="width: 582px"
                  ref="lotsAdvancedFilter"
                  :select-props="{
                    outlined: false,
                    dense: false,
                    solo: true,
                    backgroundColor: '#EAEAEA80',
                    flat: true,
                    hideDetails: true,
                    class: 'lot-filter'
                  }"
                />
                
                <v-sheet
                  style="width: 582px; border-radius: 0 0 .3em .3em; margin-top: -1px;"
                  :outlined="customReportFilters.lots && customReportFilters.lots.length > 0"
                  elevation="10"
                  class="py-4 px-1 elevation-0 chip-sheet"
                  max-width="100%"
                  v-if="customReportFilters.lots && customReportFilters.lots.length > 0"
                >
                  <v-chip-group
                    color="primary"
                    active-class="primary--text"
                    class="chip-scroll-container"
                    show-arrows
                    style="width: 100%"
                  >
                    <v-chip
                      close
                      active-class="primary--text"
                      v-for="(lot, index) in customReportFilters.lots"
                      :key="index"
                      @click:close="removeLot(lot)"
                      class="my-1"
                    >
                      {{ lot.name || lot }}
                    </v-chip>
                  </v-chip-group>
                </v-sheet>
              </v-list>

            <!-- Products Filter -->
            <span v-if="item.type === 'products'"
              class="mb-6">
              <v-autocomplete
                style="width: 582px"
                :no-data-text="$t('common.fields.noDataAvailable')"
                small
                flat
                solo
                background-color="#EAEAEA80"
                ref="productFilters"
                :label="$t('records.titles.labels.product')"
                multiple
                clearable
                item-color="primary"
                item-text="product_name"
                item-value="product_name"
                :items="productsWithSelectAll"
                v-model="customReportFilters.products"
                small-chips
                color="primary"
                @update:search-input="searchNomenclator($event, 'products')"
                :loading="loadingProducts"
                return-object
                hide-details
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip small v-if="index === 0">
                    <span>{{ item.product_name }}</span>
                  </v-chip>
                  <span @click.stop="" small v-if="index === 1" class="grey--text caption"
                    >(+{{ customReportFilters.products.length - 1 }}
                    {{ $t('common.titles.others') }})</span
                  >
                </template>
                <template v-slot:item="{ item, attrs }">
                  <div v-if="item.isSelectAllOption" style="width: 360px">
                    <v-checkbox
                      :value="allProductsSelected"
                      color="primary"
                      class="pa-2 mt-0"
                      :hide-details="true"
                      @change="toggleAllProducts"
                    >
                      <template v-slot:label>
                        <div class="font-weight-bold">{{ $t('common.titles.selectAll') }}</div>
                      </template>
                    </v-checkbox>
                    <v-divider class="my-1"></v-divider>
                  </div>
                  <div v-else style="width: 360px">
                    <v-checkbox
                      :input-value="attrs.inputValue"
                      color="primary"
                      class="pa-2 mt-0"
                      :hide-details="true"
                    >
                      <template v-slot:label>
                        <read-more
                          :classes="'mb-0'"
                          :text="item.product_name"
                          :max-chars="80"
                          :font-size="14"
                        />
                      </template>
                    </v-checkbox>
                  </div>
                </template>
              </v-autocomplete>
              <v-sheet
                style="margin-top: -1px; width: 582px; border-radius: 0 0 .3em .3em"
                :outlined="customReportFilters.products && customReportFilters.products.length > 0"
                elevation="10"
                class="py-4 px-1 elevation-0"
                max-width="100%"
              >
                <v-chip-group
                  color="primary"
                  active-class="primary--text"
                  style="width: 100%"
                >
                  <v-chip
                    close
                    active-class="primary--text"
                    v-for="product in customReportFilters.products"
                    :key="product.id"
                    @click:close="deleteNomenclator(product, 'products')"
                  >
                    {{ product.product_name || product }}
                  </v-chip>
                </v-chip-group>
              </v-sheet>
            </span>
          </v-col>
        </v-row>
      </v-list-item-content>
    </v-list-item-content>
  </v-list>
</template>

<script>
import i18n from "@/plugins/i18n";
import AdvancedFilterComponent from "@/components/common/AdvancedFilterComponent.vue";
import ReadMore from "@/components/ReadMoreComponent";
import LotsMixin from "@/mixins/lots.mixin";
import { mapActions, mapGetters } from "vuex";
import _ from 'lodash';
import sortList from '@/services/OrderListService';

export default {
  components: {
    AdvancedFilterComponent,
    ReadMore
  },
  mixins: [LotsMixin],
  data() {
    return {
      loadingProducts: false,
      selectedLots: [],
      profile: null,
      selectAllProductsItem: { isSelectAllOption: true, product_name: 'Select All' },
      filters: [
        {
          title: "reportTypes",
          icon: "mdi-file-document-outline",
          translationPath: "reports.fields.reportType",
          placeholder: "reports.fields.selectReportType",
          type: "reportTypes",
          options: [
            { type: 'QAP', title: i18n.t('reports.titles.qap-report') },
            { type: 'SUSTAINABILITY_DECLARATION', title: i18n.t('reports.titles.sustainability-declaration') }
          ]
        },
        {
          title: "uniqueId",
          icon: "mdi-identifier",
          translationPath: "reports.fields.reportNumber",
          type: "uniqueId"
        },
        {
          title: "latest",
          icon: "mdi-clock-outline",
          translationPath: "reports.fields.latest",
          placeholder: "reports.fields.selectLatest",
          type: "latest",
          options: [
            { value: true, name: i18n.t('common.buttons.yes') },
            { value: false, name: i18n.t('common.buttons.no') }
          ]
        },
        {
          title: "lots",
          icon: "mdi-view-grid-outline",
          translationPath: "reports.fields.lot-fields",
          type: "lots"
        },
        {
          title: "products",
          icon: "mdi-package-variant-closed",
          translationPath: "reports.fields.product-fields",
          type: "products"
        }
      ],
      lotsSelected: []
    };
  },
  computed: {
    ...mapGetters({
      products: 'product/products',
      customReportFilters: 'ui/GET_CUSTOM_REPORT_FILTERS'
    }),
    
    allProductsSelected() {
      if (!this.products || !this.products.length || !this.customReportFilters.products) {
        return false;
      }
      // Check if all available products are selected
      return this.products.length > 0 && 
             this.products.every(product => 
               this.customReportFilters.products.some(p => p.product_name === product.product_name)
             );
    },

    productsWithSelectAll() {
      if (!this.products) return [this.selectAllProductsItem];
      return [this.selectAllProductsItem, ...this.products];
    }
  },
  created() {
    this.profile = JSON.parse(localStorage.getItem('profile'));
    this.initFilters();
    
    // Initialize lot data from store if available
    if (this.customReportFilters.lots && this.customReportFilters.lots.length) {
      this.selectedLots = [...this.customReportFilters.lots];
    }
    
    // Initialize lots using mixin method
    this.fetchItems();
  },
  mounted() {
    // Listen for clear events coming from FiltersMenu or IndexView
    this.$root.$on("clear:selected:lots", this.clearSelectedLots);
    console.log('CustomReportFilters is listening for clear:selected:lots event');
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off("clear:selected:lots", this.clearSelectedLots);
  },
  methods: {
    ...mapActions({
      fetchListProducts: 'product/fetchListProduct'
    }),
    orderData(list, order) {
      return sortList.orderListByUppercase(list, order);
    },
    onLotDataChange(lots) {
      if (!lots) {
        this.selectedLots = [];
        this.customReportFilters.lots = [];
        return;
      }
      
      // Update the store with selected lots
      this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
        ...this.customReportFilters,
        lots: lots
      });
    },
    removeLot(lot) {
      const lotIndex = this.customReportFilters.lots.findIndex(l => 
        (l.name && lot.name && l.name === lot.name) || l === lot
      );
      
      if (lotIndex !== -1) {
        const updatedLots = [...this.customReportFilters.lots];
        updatedLots.splice(lotIndex, 1);
        
        // Update the selected lots in the filter component
        this.selectedLots = updatedLots;
        
        // Update the store
        this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
          ...this.customReportFilters,
          lots: updatedLots
        });
        
        // If removing the last lot, also reset the AdvancedFilterComponent
        if (updatedLots.length === 0 && this.$refs.lotsAdvancedFilter[0]) {
          this.$refs.lotsAdvancedFilter[0].searchQuery = '';
          this.$refs.lotsAdvancedFilter[0].filterCriteria = 'contain';
        }
      }
    },
    clearSelectedLots() {
      console.log('clearSelectedLots called in CustomReportFilters');
      this.selectedLots = [];
      
      // Reset the AdvancedFilterComponent using its ref
      if (this.$refs.lotsAdvancedFilter[0]) {
        console.log('Resetting lotsAdvancedFilter component');
        this.$refs.lotsAdvancedFilter[0].reset();
      }
      
      // Update the store with empty lots array
      this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
        ...this.customReportFilters,
        lots: []
      });

      // Ensure search query and filter criteria in the lots filter are reset
      if (this.$refs.lotsAdvancedFilter[0]) {
        this.$refs.lotsAdvancedFilter[0].searchQuery = '';
        this.$refs.lotsAdvancedFilter[0].filterCriteria = 'contain';
      }
    },
    clearSelectedLots() {
      console.log('clearSelectedLots called in CustomReportFilters');
      this.selectedLots = [];
      
      // Update the store with empty lots array
      this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
        ...this.customReportFilters,
        lots: []
      });
    },
    async searchNomenclator(event, nomenclator) {
      this.loadingProducts = true;
      const filters = {
        companyId: this.profile.company_id,
        search: event || "",
        pageableDTO: {
          page: 0,
          size: 10,
          sortBy: 'id',
          direction: 'ASC',
        }
      };

      switch (nomenclator) {
        case "products":
          await this.fetchListProducts([filters, this.$toast]).finally(() => {
            this.loadingProducts = false;
          });
          break;
      }
    },
    
    toggleAllProducts(selected) {
      if (selected) {
        // Select all products
        this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
          ...this.customReportFilters,
          products: [...this.products]
        });
      } else {
        // Deselect all products
        this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
          ...this.customReportFilters,
          products: []
        });
      }
    },
    
    deleteNomenclator(item, type) {
      if (type === 'products') {
        const updatedItems = this.customReportFilters.products.filter(p => p.product_name !== item.product_name);
        
        this.$store.commit("ui/SET_CUSTOM_REPORT_FILTER_FILTERS", {
          ...this.customReportFilters,
          products: updatedItems
        });
        
        // Update checkbox state after deletion
        this.$nextTick(() => {
          if (this.$refs.productFilters) {
            this.checkState(
              'products',
              this.$refs.productFilters.filteredItems,
              updatedItems
            );
          }
        });
      }
    },
    initFilters() {
      const savedFilters = JSON.parse(localStorage.getItem('filtersCustomReport') || '{}');
      if (savedFilters.type === 'custom-reports') {
        // If we have saved product IDs but need objects, let's fetch the product details
        if (savedFilters.products && Array.isArray(savedFilters.products) && 
            savedFilters.products.length > 0 && 
            (typeof savedFilters.products[0] !== 'object' || !savedFilters.products[0].product_name)) {
          
          const productNames = savedFilters.products;
          // First set the filters as they are
          this.$store.commit("ui/CUSTOM_REPORT_TO_FILTERS", {
            ...this.customReportFilters,
            ...savedFilters
          });
          
          // Then fetch product details to update the UI
          this.loadProductsFromIds(productNames);
        } else {
          this.$store.commit("ui/CUSTOM_REPORT_TO_FILTERS", {
            ...this.customReportFilters,
            ...savedFilters
          });
        }
        
        // Any other initialization that depends on filters being set
      }
    },
    
    async loadProductsFromIds(productNames) {
      if (!productNames || productNames.length === 0) return;
      
      this.loadingProducts = true;
      try {
        // Check if we already have product data
        if (this.products && this.products.length > 0) {
          const matchedProducts = this.products.filter(p => 
            productNames.includes(p.product_name)
          );
          
          if (matchedProducts.length > 0) {
            this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
              ...this.customReportFilters,
              products: matchedProducts
            });
          }
        } else {
          // Fetch product data if not available
          const filters = {
            companyId: this.profile.company_id,
            search: "",
            ids: productNames,
            pageableDTO: {
              page: 0,
              size: productNames.length,
              sortBy: 'id',
              direction: 'ASC',
            }
          };
          
          await this.fetchListProducts([filters, this.$toast]).then(() => {
            if (this.products && this.products.length > 0) {
              this.$store.commit("ui/SET_CUSTOM_REPORT_FILTERS", {
                ...this.customReportFilters,
                products: this.products.filter(p => productNames.includes(p.product_name))
              });
            }
          });
        }
      } catch (error) {
        console.error("Error loading products from IDs:", error);
      } finally {
        this.loadingProducts = false;
      }
    },
    handleSearch({ query, criteria }) {
      this.searchQuery = query || '';
      this.selectedFilter = criteria || 'contain';
      this.pageableDTO.page = 0;
      
      console.log('Search triggered:', { query, criteria });
      this.fetchItems();
    },
    
    handlePageChange({ page, size }) {
      this.pageableDTO.page = page;
      if (size) this.pageableDTO.size = size;
      this.fetchItems();
    },
  }
};
</script>

<style scoped>
:deep(.v-slide-group__prev),
:deep(.v-slide-group__next) {
  min-width: 0px !important;
}

span :deep(.v-chip-group .v-chip--active) {
  color: #4b4b4b !important;
}

.chip-sheet {
  overflow: hidden;
}

.chip-scroll-container {
  overflow-x: auto;
  display: flex;
  flex-wrap: nowrap;
  padding: 0 8px;
}

/* Improve scrollbar appearance */
.chip-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.chip-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.chip-scroll-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.chip-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

:deep(.chip-scroll-container .v-slide-group__content) {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
}

.lot-filter :deep(.v-input__slot) {
  min-height: 40px;
  width: 100%;
}

.lot-filter :deep(.v-select__selections) {
  padding: 7px 0;
}
</style>
